import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';

import type {
  LogoSubidoResponse,
  OrganizacionPerfil,
  OrganizacionPerfilActualizarBody,
} from '../models/organizacion.model';
import { MSG_SIN_CONEXION, mensajeApiAmigable } from '../constants/mensajes-usuario';
import { API_BASE_URL } from '../tokens/api-base-url';

/** Contrato esperado frente al backend multitenant (`backend.md`). */
const RUTA_PERFIL = `/organizaciones/perfil`;

@Injectable({ providedIn: 'root' })
export class OrganizacionesService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);

  readonly perfil = signal<OrganizacionPerfil | null>(null);
  readonly cargandoPerfil = signal(false);
  readonly guardandoEmpresa = signal(false);
  readonly subiendoLogo = signal(false);
  readonly errorEmpresa = signal<string | null>(null);

  private mapErr(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const b = err.error as { msg?: string; message?: string } | string | null;
      if (typeof b === 'string' && b.trim()) return mensajeApiAmigable(b);
      if (b && typeof b === 'object') {
        if (typeof (b as { msg?: string }).msg === 'string') {
          return mensajeApiAmigable((b as { msg: string }).msg);
        }
        if (typeof (b as { message?: string }).message === 'string') {
          return (b as { message: string }).message;
        }
      }
      if (err.status === 404) return 'Esta función no está disponible todavía.';
      if (err.status === 403) return 'No tenés permiso para cambiar estos datos.';
    }
    return MSG_SIN_CONEXION;
  }

  /** Vaciar caché (p. ej. al cerrar sesión). */
  limpiarPerfilYCaché(): void {
    this.perfil.set(null);
    this.errorEmpresa.set(null);
  }

  /** Carga fusionada con perfil anterior (no borra firma/logo recién guardado ante carreras de red). Para renovar `logo_display_url` (~caducidad firma), volvé a llamar cuando el usuario reaparezca en la app. */
  cargarPerfil(): void {
    if (this.cargandoPerfil()) return;

    this.cargandoPerfil.set(true);
    this.errorEmpresa.set(null);

    const url = `${this.base}${RUTA_PERFIL}`;
    this.http.get<unknown>(url).pipe(finalize(() => this.cargandoPerfil.set(false))).subscribe({
      next: (raw) => {
        /**
         * Si el GET termina después del POST del logo, la respuesta suele traer sin `logo_url`
         * y el normalizador llenaba `null`, borrando el logo en pantalla. Fusionamos sin pisar
         * campos buenos con vacíos/null.
         */
        this.perfil.set(mergearPerfilRespuestaApi(this.perfil(), normalizarPerfilApi(raw)));
      },
      error: (e: unknown) => this.errorEmpresa.set(this.mapErr(e)),
    });
  }

  /** Actualiza texto del negocio (`PATCH /organizaciones/perfil`). Si el PATCH no devuelve cuerpo, recargamos con GET. */
  guardarDatosEmpresa(body: OrganizacionPerfilActualizarBody): Observable<unknown> {
    this.guardandoEmpresa.set(true);
    this.errorEmpresa.set(null);
    const url = `${this.base}${RUTA_PERFIL}`;
    return this.http.patch<unknown>(url, body).pipe(
      finalize(() => this.guardandoEmpresa.set(false)),
      tap({
        next: (raw) => {
          const parsed = normalizarPerfilApi(raw);
          const tieneEmpresaTxt =
            parsed.nombre != null ||
            parsed.nit != null ||
            parsed.direccion != null ||
            parsed.telefono != null ||
            parsed.correo != null;
          if (tieneEmpresaTxt) {
            this.perfil.set(mergearPerfilRespuestaApi(this.perfil(), parsed));
          } else {
            this.cargarPerfil();
          }
        },
        error: (e: unknown) => this.errorEmpresa.set(this.mapErr(e)),
      }),
    );
  }

  /**
   * `POST /organizaciones/logo` — multipart con el archivo en `logo`.
   * Ver `backend.md`; requiere permiso ORGANIZACION:EDITAR (perfil y logo ya no van con CONFIG).
   */
  subirLogoArchivo(file: File): Observable<LogoSubidoResponse> {
    this.subiendoLogo.set(true);
    this.errorEmpresa.set(null);
    const fd = new FormData();
    fd.append('logo', file, file.name);
    const url = `${this.base}/organizaciones/logo`;
    return this.http.post<LogoSubidoResponse>(url, fd).pipe(
      finalize(() => this.subiendoLogo.set(false)),
      tap({
        next: (res) =>
          this.perfil.update((prev) =>
            mergearPerfilRespuestaApi(prev, {
              logo_url: res.logo_url ?? prev?.logo_url ?? null,
              /** Firmada para UI; si el servidor no envía display, mantenemos la anterior hasta un GET de perfil. */
              logo_display_url: res.logo_display_url ?? prev?.logo_display_url ?? null,
            }),
          ),
        error: (e: unknown) => this.errorEmpresa.set(this.mapErr(e)),
      }),
    );
  }
}

/**
 * Combina lo que ya teníamos en memoria (p. ej. logo recién subido) con la respuesta
 * del API sin pisar textos ni URLs con `null` / vacío cuando el backend omite campos.
 */
function mergearPerfilRespuestaApi(
  anterior: OrganizacionPerfil | null,
  incoming: OrganizacionPerfil,
): OrganizacionPerfil {
  const base = { ...(anterior ?? {}) } as OrganizacionPerfil;
  const keys: (keyof OrganizacionPerfil)[] = [
    'id',
    'slug',
    'nombre',
    'nit',
    'direccion',
    'telefono',
    'correo',
    'logo_url',
    'logo_display_url',
    'estado',
    'creado_en',
  ];
  for (const k of keys) {
    const v = incoming[k];
    if (k === 'id' && typeof v === 'number' && Number.isFinite(v)) {
      base.id = v;
      continue;
    }
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      (base as Record<string, unknown>)[k as string] = v;
    }
  }
  return base;
}

/** Acepta cuerpo plano o anidado bajo `organizacion`. */
export function normalizarOrganizacionPerfilDesdeApi(raw: unknown): OrganizacionPerfil {
  return normalizarPerfilApi(raw);
}

/** Acepta cuerpo plano o anidado bajo `organizacion`. */
function normalizarPerfilApi(raw: unknown): OrganizacionPerfil {
  if (raw == null || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const nested =
    (r['organizacion'] ?? r['organization'] ?? r['data']) as Record<string, unknown> | undefined;
  const o = (nested && typeof nested === 'object') ? nested : r;
  const str = (k: string): string | null => {
    const v = o[k];
    if (v == null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };
  const idRaw = o['id'] ?? o['organizacion_id'];
  const num = typeof idRaw === 'number' && Number.isFinite(idRaw) ? idRaw : null;
  return {
    id: num,
    slug: str('slug'),
    nombre: str('nombre') ?? str('nombre_comercial'),
    nit: str('nit') ?? str('nit_empresa') ?? str('documento'),
    direccion: str('direccion'),
    telefono: str('telefono'),
    correo: str('correo') ?? str('email'),
    logo_url: str('logo_url'),
    logo_display_url: str('logo_display_url'),
    estado: str('estado'),
    creado_en: str('creado_en'),
  };
}
